import APP_CONFIG from '@/config/app';
import { NewsInterface } from '@/interfaces';
import NewsService from '@/services/NewsService';
import reactQueryClient from '@/services/ReactQueryClientService';
import { logError } from '@/utils/logger';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
interface NewsContextType {
  news: NewsInterface[];
  isLoading: boolean;
  error: Error | null;
  refreshNews: () => void;
}
const NewsContext = createContext<NewsContextType | undefined>(undefined);
export const useNews = (limit?: number) => {
  const context = useContext(NewsContext);
  if (!context) {
    throw new Error('useNews must be used within a NewsProvider');
  }
  const limitedNews = limit ? context.news.slice(0, limit) : context.news;
  return { ...context, news: limitedNews };
};
interface NewsProviderProps {
  children: ReactNode;
}
export const NewsProvider: React.FC<NewsProviderProps> = ({ children }) => {
  const {
    data: news = [],
    error,
    isLoading,
    refetch: refreshNews,
  } = useQuery({
    queryKey: ['news'],
    queryFn: () =>
      APP_CONFIG.SHOW_NEWS ? NewsService.fetchNews() : Promise.resolve([]),
    staleTime: 6 * 60 * 60 * 1000,
    retry: 1,
    enabled: APP_CONFIG.SHOW_NEWS,
  });
  const value = useMemo(
    () => ({
      news,
      isLoading: APP_CONFIG.SHOW_NEWS ? isLoading : false,
      error: error || null,
      refreshNews,
    }),
    [news, isLoading, error, refreshNews],
  );
  if (error) {
    logError(error, 'NewsProvider');
  }
  return (
    <QueryClientProvider client={reactQueryClient}>
      <NewsContext.Provider value={value}>{children}</NewsContext.Provider>
    </QueryClientProvider>
  );
};
